import { Injectable } from '@angular/core';
import { Cloudinary } from '@cloudinary/url-gen';
import { format, quality, dpr } from '@cloudinary/url-gen/actions/delivery';
import { scale } from '@cloudinary/url-gen/actions/resize';
import { auto as autoFormat } from '@cloudinary/url-gen/qualifiers/format';
import { auto as autoQuality } from '@cloudinary/url-gen/qualifiers/quality';
import { auto as autoDpr } from '@cloudinary/url-gen/qualifiers/dpr';

@Injectable({
  providedIn: 'root'
})
export class CloudinaryService {
  private cloudinary: Cloudinary;

  constructor() {
    this.cloudinary = new Cloudinary({
      cloud: {
        cloudName: 'dmctlj20b'
      }
    });
  }

  /**
   * Get optimized image URL for critical assets
   */
  getCriticalImageUrl(publicId: string, width?: number): string {
    let image = this.cloudinary.image(publicId)
      .delivery(format(autoFormat()))        // Auto format (WebP/AVIF)
      .delivery(quality(autoQuality()))      // Auto quality
      .delivery(dpr(autoDpr()));             // Auto DPR for retina

    if (width) {
      image = image.resize(scale().width(width));
    }

    return image.toURL();
  }

  /**
   * Get optimized image URL for lazy loaded assets
   */
  getLazyImageUrl(publicId: string, width?: number): string {
    let image = this.cloudinary.image(publicId)
      .delivery(format(autoFormat()))
      .delivery(quality(autoQuality()))
      .delivery(dpr(autoDpr()));

    if (width) {
      image = image.resize(scale().width(width));
    }

    return image.toURL();
  }

  /**
   * Generate responsive srcset string for an image.
   * Produces URLs at multiple widths for the browser to choose from.
   */
  getResponsiveSrcSet(publicId: string, widths: number[] = [320, 640, 960, 1280, 1920]): string {
    return widths
      .map(w => {
        const url = this.cloudinary.image(publicId)
          .delivery(format(autoFormat()))
          .delivery(quality(autoQuality()))
          .resize(scale().width(w))
          .toURL();
        return `${url} ${w}w`;
      })
      .join(', ');
  }

  /**
   * Get thumbnail URL for progressive loading
   */
  getThumbnailUrl(publicId: string): string {
    return this.cloudinary.image(publicId)
      .delivery(format(autoFormat()))
      .delivery(quality(autoQuality()))
      .resize(scale().width(50))
      .toURL();
  }

  /**
   * Upload images to Cloudinary
   */
  async uploadImage(file: File, folder: string = 'angular-app'): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', 'your_upload_preset');
    formData.append('folder', folder);

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${this.getCloudName()}/image/upload`,
        {
          method: 'POST',
          body: formData
        }
      );
      const data = await response.json();
      return data.public_id;
    } catch (error) {
      console.error('Upload failed:', error);
      throw error;
    }
  }

  private getCloudName(): string {
    return 'dmctlj20b';
  }
}
